import type { EditType, GistType } from "../../services/types";

const textFromHtml = (html: string): string =>
  html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

const byCreatedAt = <T extends { createdAt?: string | null }>(a: T, b: T): number =>
  new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime();

export const getLatestEdit = (gist: Pick<GistType, "versions"> | null | undefined): EditType | null => {
  const latestVersion = [...(gist?.versions || [])].sort(byCreatedAt).at(-1);
  return [...(latestVersion?.edits || [])].sort(byCreatedAt).at(-1) || null;
};

export const getLatestBody = (gist: Pick<GistType, "versions"> | null | undefined): string =>
  getLatestEdit(gist)?.body || "";

export const getExcerpt = (gist: Pick<GistType, "versions"> | null | undefined, maxLength = 180): string => {
  const text = textFromHtml(getLatestBody(gist));
  if (!text) return "No public body text yet.";
  return text.length > maxLength ? `${text.slice(0, maxLength - 1).trim()}…` : text;
};

export const getDisplayName = (user: { displayName?: string | null; name?: string | null; handle?: string | null } | null | undefined): string =>
  user?.displayName || user?.name || user?.handle || "Unknown user";


export type PublicSeoMeta = {
  title: string;
  description?: string | null;
  canonicalPath?: string | null;
  indexable?: boolean;
};

const ensureMeta = (selector: string, attributes: Record<string, string>): HTMLMetaElement => {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element?.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
};

const ensureCanonical = (): HTMLLinkElement => {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }
  return element;
};

export const absoluteBrowserUrl = (path: string | null | undefined): string => {
  const normalized = path || "/";
  if (/^https?:\/\//i.test(normalized)) return normalized;
  const withSlash = normalized.startsWith("/") ? normalized : `/${normalized}`;
  return `${window.location.origin}${withSlash}`;
};

export const applyPublicSeoMeta = ({ title, description, canonicalPath, indexable = true }: PublicSeoMeta): void => {
  if (typeof document === "undefined") return;

  const fullTitle = title.includes("Gistoscope") ? title : `${title} · Gistoscope`;
  const safeDescription = description || "A public Gistoscope page.";
  const canonicalUrl = absoluteBrowserUrl(canonicalPath || window.location.pathname);

  document.title = fullTitle;
  ensureCanonical().setAttribute("href", canonicalUrl);
  ensureMeta('meta[name="description"]', { name: "description" }).setAttribute("content", safeDescription);
  ensureMeta('meta[name="robots"]', { name: "robots" }).setAttribute("content", indexable ? "index,follow" : "noindex,nofollow");
  ensureMeta('meta[property="og:title"]', { property: "og:title" }).setAttribute("content", fullTitle);
  ensureMeta('meta[property="og:description"]', { property: "og:description" }).setAttribute("content", safeDescription);
  ensureMeta('meta[property="og:url"]', { property: "og:url" }).setAttribute("content", canonicalUrl);
  ensureMeta('meta[property="og:type"]', { property: "og:type" }).setAttribute("content", "article");
};

export const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to legacy copy fallback.
  }

  try {
    const textArea = document.createElement("textarea");
    textArea.value = value;
    textArea.setAttribute("readonly", "true");
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(textArea);
    return ok;
  } catch {
    return false;
  }
};
